import * as React from 'react';
import { Heading, Text } from '@/hen-ui/atomics';
import { Flex } from '@/hen-ui/composites';
import { Icon, type IconName } from '@/hen-ui/atomics/Icon';
import { datapointVariants } from './datapoint.variants';

/**
 * Props for the DataPoint composite.
 * All styling uses design tokens via Icon, Heading, and Text atomics.
 */
export interface DataPointProps {
  /** Visual style variant. Default: "primary" */
  style?: 'primary' | 'light' | 'secondary' | 'borderless';
  /** Size variant; lg = vertical with subtext/context, sm = horizontal compact. Default: "lg" */
  size?: 'sm' | 'lg';
  /** Optional Lucide icon name */
  icon?: IconName;
  /** Optional main title (lg) or fallback label (sm when property not set) */
  title?: string;
  /** Optional subtitle; lg size only */
  subtext?: string;
  /** Primary data value (required) */
  value: string | number;
  /** Optional context text; lg size only */
  context?: string;
  /** Optional property label; sm size only (used instead of title) */
  property?: string;
}

/** Root layout: vertical stack with consistent gap. Forwards ref and div props. */
interface WrapperProps
  extends React.PropsWithChildren,
    React.HTMLAttributes<HTMLDivElement> {}

const DataPointWrapper = React.forwardRef<HTMLDivElement, WrapperProps>(
  ({ className, children, ...rest }, ref) => (
    <Flex
      ref={ref}
      className={className}
      direction='column'
      align='start'
      gap='sm'
      {...rest}
    >
      {children}
    </Flex>
  ),
);
DataPointWrapper.displayName = 'DataPointWrapper';

/** LG size: vertical layout — icon, title, subtext, value, context (all optional except value). */
interface LargeContentProps {
  title: DataPointProps['title'];
  subtext: DataPointProps['subtext'];
  context: DataPointProps['context'];
  icon: DataPointProps['icon'];
  value: DataPointProps['value'];
  labelId?: string;
}
const LargeContent: React.FC<LargeContentProps> = ({
  title,
  subtext,
  context,
  icon,
  value,
}) => (
  <>
    {/* Decorative; title/value convey the same info for screen readers. */}
    {icon && <Icon icon={icon} size='md' aria-hidden />}
    {title && <Heading variant='h5'>{title}</Heading>}
    {subtext && (
      <Text variant='b5' color='subtext'>
        {subtext}
      </Text>
    )}
    <Text variant='b1' tag='b'>
      {value}
    </Text>
    {context && (
      <Text variant='b5' color='subtext'>
        {context}
      </Text>
    )}
  </>
);

/** SM size: horizontal compact layout — optional label (property or title), then icon + value. */
interface SmallContentProps {
  title: DataPointProps['title'];
  property: DataPointProps['property'];
  icon: DataPointProps['icon'];
  value: DataPointProps['value'];
  labelId?: string;
}

const SmallContent: React.FC<SmallContentProps> = ({
  title,
  property,
  icon,
  value,
}) => {
  return (
    <>
      {title && <Heading variant='h6'>{title}</Heading>}
      <Flex direction='row' align='start' gap='xxs'>
        {icon && (
          <Flex
            direction='row'
            justify='center'
            align='center'
            gap='xs'
            className='w-7 h-7 bg-neutral-black rounded-lg shrink-0 p-sm'
          >
            <Icon color='white' icon={icon} size='sm' aria-hidden />
          </Flex>
        )}
        <Flex direction='column' gap='xxs'>
          {property && (
            <Text variant='l1' color='subtext'>
              {property}
            </Text>
          )}
          <Text variant='b3' className='font-bold'>
            {value}
          </Text>
        </Flex>
      </Flex>
    </>
  );
};

/**
 * DataPoint — composite for a single metric or statistic.
 *
 * Uses Icon, Heading, and Text atomics inside a styled container. Supports
 * style variants (primary, light, secondary, borderless) and sizes (lg vertical,
 * sm horizontal). Optional props render only when provided.
 *
 * @see DataPoint.stories.tsx for all variants and sizes
 */
export const DataPoint = React.forwardRef<HTMLDivElement, DataPointProps>(
  (
    {
      style = 'primary',
      size = 'lg',
      icon,
      title,
      subtext,
      value,
      context,
      property,
      ...rest
    },
    ref,
  ) => {
    const ContentComponent = size === 'lg' ? LargeContent : SmallContent;
    // Name the group for a11y when we have a visible label or value.
    const hasLabel =
      size === 'lg' ? !!(title || value) : !!((property ?? title) || value);
    return (
      <DataPointWrapper
        ref={ref}
        className={datapointVariants({ style, size })}
        role='group'
        {...rest}
      >
        <ContentComponent
          icon={icon}
          title={title}
          subtext={subtext}
          context={context}
          property={property}
          value={value}
        />
      </DataPointWrapper>
    );
  },
);
DataPoint.displayName = 'DataPoint';
